import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react"
import { createFileRoute, Link, Outlet, useLocation } from "@tanstack/react-router"
import { BookOpen, MagnifyingGlass } from "@phosphor-icons/react"
import { BreadcrumbWrapper } from "~/components/reusable/Breadcrumb"
import { Header } from "~/components/reusable/Header"
import { Input } from "~/components/reusable/Input"
import {
  DOCS_PATH,
  MIN_SEARCH_QUERY_LEN,
  categoryLandings,
  docsByCategory,
  docsBySlug,
  searchDocs,
} from "~/utils/docs"
import { cn } from "~/utils/helpers"

export const Route = createFileRoute("/_auth/internal/docs/_layout")({
  staticData: { skipSkuStoreData: true },
  component: DocsLayout,
})

function DocsLayout() {
  const { pathname } = useLocation()
  const [query, setQuery] = useState("")
  const trimmed = query.trim()
  const deferredQuery = useDeferredValue(trimmed)
  const searchRef = useRef<HTMLInputElement>(null)

  const hits = useMemo(
    () => (deferredQuery.length >= MIN_SEARCH_QUERY_LEN ? searchDocs(deferredQuery) : []),
    [deferredQuery],
  )

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return
      const target = e.target as HTMLElement | null
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target?.isContentEditable
      ) {
        return
      }
      e.preventDefault()
      searchRef.current?.focus()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [])

  const breadcrumbItems = useMemo(() => {
    const slugMatch = pathname.match(/^\/internal\/docs\/([^/]+)$/)
    const doc = slugMatch ? docsBySlug.get(slugMatch[1]!) : null
    if (!doc) return [{ label: "Docs" }]
    return [{ label: "Docs", href: DOCS_PATH }, { label: doc.category }, { label: doc.title }]
  }, [pathname])

  const showSearchResults = trimmed.length >= MIN_SEARCH_QUERY_LEN

  return (
    <div className="flex flex-col h-full">
      <Header>
        <BreadcrumbWrapper items={breadcrumbItems} />
      </Header>

      <div className="flex flex-1 min-h-0">
        <nav
          aria-label="Docs navigation"
          className="w-64 shrink-0 border-r border-base overflow-y-auto py-4 px-2 bg-subtle"
        >
          <Link
            to={DOCS_PATH}
            className="flex items-center gap-2 px-3 pb-3 text-sm font-medium text-fg-subtle hover:text-fg"
          >
            <BookOpen size={16} />
            Internal Docs
          </Link>

          <div className="px-1 pb-3">
            <Input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && setQuery("")}
              placeholder="Search docs…"
              aria-label="Search docs"
              small
              containerClassName="w-full"
              className="pl-7"
              icon={
                <MagnifyingGlass
                  size={14}
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-fg-subtle pointer-events-none"
                />
              }
            />
          </div>

          {showSearchResults ? (
            <SearchResults hits={hits} pathname={pathname} query={trimmed} />
          ) : (
            <CategoryList pathname={pathname} />
          )}
        </nav>

        <main className="flex-1 overflow-y-auto scroll-smooth">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

function CategoryList({ pathname }: { pathname: string }) {
  return (
    <div className="flex flex-col gap-3">
      {docsByCategory.map(({ category, docs }) => {
        const landing = categoryLandings.get(category)
        const landingActive = landing && pathname === `${DOCS_PATH}/${landing.slug}`
        return (
          <div key={category}>
            {landing ? (
              <Link
                to="/internal/docs/$slug"
                params={{ slug: landing.slug }}
                aria-current={landingActive ? "page" : undefined}
                className={cn(
                  "block px-3 pt-2 pb-1 text-xs uppercase tracking-wide hover:text-fg",
                  landingActive ? "text-fg font-semibold" : "text-fg-subtle",
                )}
              >
                {category}
              </Link>
            ) : (
              <div className="px-3 pt-2 pb-1 text-xs uppercase tracking-wide text-fg-subtle">
                {category}
              </div>
            )}
            <ul className="flex flex-col gap-0.5">
              {docs.map((doc) => {
                const active = pathname === `${DOCS_PATH}/${doc.slug}`
                return (
                  <li key={doc.slug}>
                    <Link
                      to="/internal/docs/$slug"
                      params={{ slug: doc.slug }}
                      aria-current={active ? "page" : undefined}
                      className={cn(
                        "block rounded px-3 py-1.5 text-sm hover:bg-base",
                        active ? "bg-base font-medium text-fg" : "text-fg-subtle",
                      )}
                    >
                      {doc.title}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>
        )
      })}
    </div>
  )
}

function HighlightedText({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>
  const lowerText = text.toLowerCase()
  const lowerQuery = query.toLowerCase()
  const parts: React.ReactNode[] = []
  let i = 0
  while (i < text.length) {
    const idx = lowerText.indexOf(lowerQuery, i)
    if (idx < 0) {
      parts.push(text.slice(i))
      break
    }
    if (idx > i) parts.push(text.slice(i, idx))
    parts.push(
      <span key={idx} className="font-semibold text-fg bg-highlight">
        {text.slice(idx, idx + query.length)}
      </span>,
    )
    i = idx + query.length
  }
  return <>{parts}</>
}

function SearchResults({
  hits,
  pathname,
  query,
}: {
  hits: ReturnType<typeof searchDocs>
  pathname: string
  query: string
}) {
  if (hits.length === 0) {
    return (
      <div className="px-3 py-4 text-sm text-fg-subtle">
        No docs match <span className="font-medium text-fg">&ldquo;{query}&rdquo;</span>
      </div>
    )
  }
  return (
    <>
      <div className="px-3 pb-2 text-xs text-fg-subtle">
        {hits.length} {hits.length === 1 ? "doc" : "docs"} match
      </div>
      <ul className="flex flex-col gap-0.5">
        {hits.map(({ doc, matchIn, snippet }) => {
          const active = pathname === `${DOCS_PATH}/${doc.slug}`
          return (
            <li key={doc.slug}>
              <Link
                to="/internal/docs/$slug"
                params={{ slug: doc.slug }}
                aria-current={active ? "page" : undefined}
                className={cn(
                  "block rounded px-3 py-1.5 text-sm hover:bg-base",
                  active ? "bg-base font-medium text-fg" : "text-fg-subtle",
                )}
              >
                <div className="font-medium text-fg truncate">
                  <HighlightedText text={doc.title} query={query} />
                </div>
                {matchIn !== "title" && (
                  <div className="mt-0.5 text-xs text-fg-subtle line-clamp-2">
                    <HighlightedText text={snippet} query={query} />
                  </div>
                )}
              </Link>
            </li>
          )
        })}
      </ul>
    </>
  )
}
